import React, { useState, useEffect } from "react";
import {
  Search, Plus, Edit2, Trash2, X, ChevronsLeft, ChevronsRight,
  ArrowRightCircle, Ban, Infinity as InfinityIcon, Settings, Save,
  RotateCcw, Pencil, Check,
} from "lucide-react";
import { MdKeyboardArrowLeft, MdKeyboardArrowRight } from "react-icons/md";
import { useLeaveTypes } from "../../context/LeaveTypeContext"; // adjust path

// ---------------------------------------------------------------------------
// Carry-forward policy options.
// Different companies run different rules for the same "leave type" concept:
//   - "none"      : unused days lapse at month end, no rollover at all
//   - "unlimited" : days always roll over to next month (optionally capped)
//   - "expiring"  : days roll over, but must be used within N months
//                   (e.g. 6 months) or they are forfeited automatically
//
// This page now owns BOTH:
//   1) the company-wide default for these three settings, and
//   2) per-leave-type overrides (a leave type with null fields simply
//      inherits whatever the company default says).
// That used to live on a separate "Company Leave Policy" settings page —
// it's folded in here so everything about carry-forward lives in one place.
//
// API field names on the leave type record (as returned by /leave-types):
//   carry_forward_policy  -> "none" | "unlimited" | "expiring" | null
//   carry_forward_count   -> max days that can be carried forward | null
//   expiry_exp            -> months before carried-forward days expire | null
// ---------------------------------------------------------------------------
const CARRY_FORWARD_POLICIES = [
  { value: "none", label: "No carry forward", hint: "Unused days lapse every month.", icon: Ban },
  { value: "unlimited", label: "Unlimited carry forward", hint: "Rolls over every month, optionally capped.", icon: InfinityIcon },
  { value: "expiring", label: "Expires after N months", hint: "Rolls over but must be used within a set window.", icon: ArrowRightCircle },
];

// Wire-up note: replace this useState with your real OrgSettingsContext
// (fetchOrgPolicy / updateOrgPolicy), scoped by company/tenant id.
const ORG_POLICY_DEFAULTS = {
  policy: "expiring",
  expiry_months: 6,
  max_carry_forward: 4,
};

function policyMeta(value) {
  return CARRY_FORWARD_POLICIES.find((p) => p.value === value) || CARRY_FORWARD_POLICIES[0];
}

// Shared resolver — same logic the carry-forward ledger / leave request
// validation / payroll export screens should use, so every screen agrees.
// Reads the new API field names (carry_forward_count / expiry_exp) off the
// leave type, falling back to the company-wide default when they're null.
export function resolveEffectivePolicy(leaveType, orgPolicy) {
  const policy = leaveType.carry_forward_policy ?? orgPolicy.policy;
  return {
    policy,
    carry_forward_count: leaveType.carry_forward_count ?? orgPolicy.max_carry_forward ?? null,
    expiry_exp:
      policy === "expiring" ? leaveType.expiry_exp ?? orgPolicy.expiry_months : null,
  };
}

export default function LeaveTypes() {
  const {
    leaveTypes,
    addLeaveType,
    updateLeaveType,
    deleteLeaveType,
    fetchLeaveTypes,
    loading,
    error,
  } = useLeaveTypes();

  // ---------------------------------------------------------------------
  // Company-wide carry-forward default
  // ---------------------------------------------------------------------
  const [orgPolicy, setOrgPolicy] = useState(ORG_POLICY_DEFAULTS);
  const [savedOrgPolicy, setSavedOrgPolicy] = useState(ORG_POLICY_DEFAULTS);
  const [orgPolicySaved, setOrgPolicySaved] = useState(false);
  const isOrgPolicyDirty = JSON.stringify(orgPolicy) !== JSON.stringify(savedOrgPolicy);

  const handleSaveOrgPolicy = () => {
    // TODO: call your API, e.g. updateOrgPolicy(companyId, orgPolicy)
    setSavedOrgPolicy(orgPolicy);
    setOrgPolicySaved(true);
    setTimeout(() => setOrgPolicySaved(false), 2000);
  };

  // ---------------------------------------------------------------------
  // Leave type list state (search / filter / pagination / basic CRUD)
  // ---------------------------------------------------------------------
  const [showModal, setShowModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [filterStatus, setFilterStatus] = useState("All");
  const [currentLeave, setCurrentLeave] = useState(null);
  const [deleteId, setDeleteId] = useState(null);
  const [page, setPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(5);

  // Inline carry-forward override editing, per row (mirrors the old
  // per-type override table from the company policy settings page).
  const [editingCfId, setEditingCfId] = useState(null);
  const [cfDraft, setCfDraft] = useState(null);

  const statusColors = {
    active: "bg-green-100 text-green-600",
    inactive: "bg-red-100 text-red-600",
  };

  useEffect(() => {
    fetchLeaveTypes();
  }, []);

  const filteredLeaveTypes = leaveTypes.filter((type) => {
    const matchesStatus = filterStatus === "All" || type.status === filterStatus;
    const matchesSearch =
      type.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      type.description?.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesStatus && matchesSearch;
  });

  const totalPages = Math.ceil(filteredLeaveTypes.length / itemsPerPage);
  const paginatedLeaveTypes = filteredLeaveTypes.slice(
    (page - 1) * itemsPerPage,
    page * itemsPerPage
  );

  // ---------------------------------------------------------------------
  // Basic leave type CRUD (name / monthly limit / description / status).
  // Carry-forward is no longer set here — a brand new leave type simply
  // inherits the company default until someone overrides it in the table.
  // ---------------------------------------------------------------------
  const handleSubmit = async (e) => {
    e.preventDefault();
    const form = e.target;
    const name = form.name.value.trim();
    const monthly_limit = form.monthly_limit.value;
    const description = form.desc.value.trim();
    const status = form.status.value;

    if (!name || !description) return alert("Please fill all fields");

    const payload = {
      name,
      monthly_limit,
      description,
      status,
      // preserve any existing overrides when editing; new types start
      // with no overrides (i.e. they follow the company default)
      carry_forward_policy: currentLeave?.carry_forward_policy ?? null,
      carry_forward_count: currentLeave?.carry_forward_count ?? null,
      expiry_exp: currentLeave?.expiry_exp ?? null,
    };

    if (currentLeave) {
      await updateLeaveType(currentLeave.id, payload);
    } else {
      await addLeaveType(payload);
    }

    setShowModal(false);
    setCurrentLeave(null);
  };

  const handleDelete = async () => {
    await deleteLeaveType(deleteId);
    setShowDeleteModal(false);
    setDeleteId(null);
  };

  // ---------------------------------------------------------------------
  // Per-row carry-forward override editing
  // ---------------------------------------------------------------------
  const startCfOverride = (type) => {
    setEditingCfId(type.id);
    setCfDraft({
      carry_forward_policy: type.carry_forward_policy ?? savedOrgPolicy.policy,
      carry_forward_count: type.carry_forward_count ?? savedOrgPolicy.max_carry_forward ?? "",
      expiry_exp: type.expiry_exp ?? savedOrgPolicy.expiry_months ?? "",
    });
  };

  const cancelCfOverride = () => {
    setEditingCfId(null);
    setCfDraft(null);
  };

  const saveCfOverride = async (type) => {
    const payload = {
      ...type,
      carry_forward_policy: cfDraft.carry_forward_policy,
      carry_forward_count: cfDraft.carry_forward_count === "" ? null : Number(cfDraft.carry_forward_count),
      expiry_exp:
        cfDraft.carry_forward_policy === "expiring"
          ? Number(cfDraft.expiry_exp) || null
          : null,
    };
    await updateLeaveType(type.id, payload);
    setEditingCfId(null);
    setCfDraft(null);
  };

  const resetCfToDefault = async (type) => {
    await updateLeaveType(type.id, {
      ...type,
      carry_forward_policy: null,
      carry_forward_count: null,
      expiry_exp: null,
    });
  };

  return (
    <>
      <div className="bg-sky-50">
        <div className="px-9 py-5 max-md:p-4">
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-800 mb-5">Leave Types</h1>

          {/* ------------------------------------------------------------- */}
          {/* COMPANY-WIDE CARRY FORWARD DEFAULT                            */}
          {/* ------------------------------------------------------------- */}
          <div className="bg-white rounded-xl shadow-sm p-4 sm:p-5 mb-6">
            <div className="flex items-center gap-2 mb-1">
              <Settings className="h-5 w-5 text-gray-700" />
              <h2 className="font-semibold text-gray-700">Company Default Carry Forward</h2>
            </div>
            <p className="text-sm text-gray-500 mb-4">
              This is the standard every leave type below follows automatically, unless it has
              its own override set in the table.
            </p>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {CARRY_FORWARD_POLICIES.map((opt) => {
                const Icon = opt.icon;
                const active = orgPolicy.policy === opt.value;
                return (
                  <label
                    key={opt.value}
                    className={`flex items-start gap-2 border rounded-lg px-3 py-3 cursor-pointer transition-colors ${
                      active ? "border-sky-400 bg-sky-50" : "border-gray-200 bg-white hover:bg-gray-50"
                    }`}
                  >
                    <input
                      type="radio"
                      className="mt-1"
                      checked={active}
                      onChange={() => setOrgPolicy((p) => ({ ...p, policy: opt.value }))}
                    />
                    <span>
                      <span className="flex items-center gap-1.5 text-sm font-medium text-gray-800">
                        <Icon className="h-4 w-4" /> {opt.label}
                      </span>
                      <span className="block text-xs text-gray-500 mt-0.5">{opt.hint}</span>
                    </span>
                  </label>
                );
              })}
            </div>

            <div className="flex flex-col sm:flex-row gap-4 mt-5">
              {orgPolicy.policy !== "none" && (
                <div className="flex-1">
                  <label className="block text-xs font-medium text-gray-600 mb-1">
                    Max days that can be carried forward{" "}
                    <span className="text-gray-400">(optional cap, applies company-wide)</span>
                  </label>
                  <input
                    type="number"
                    min="0"
                    step="0.5"
                    value={orgPolicy.max_carry_forward ?? ""}
                    onChange={(e) =>
                      setOrgPolicy((p) => ({
                        ...p,
                        max_carry_forward: e.target.value === "" ? null : Number(e.target.value),
                      }))
                    }
                    placeholder="No cap"
                    className="w-full border rounded-lg px-3 py-2 text-sm outline-none transition-all focus:ring-2 focus:ring-[#8c57ff]/40 focus:border-[#8c57ff]/40"
                  />
                </div>
              )}

              {orgPolicy.policy === "expiring" && (
                <div className="flex-1">
                  <label className="block text-xs font-medium text-gray-600 mb-1">
                    Carried-forward days expire after
                  </label>
                  <div className="flex items-center gap-2">
                    <input
                      type="number"
                      min="1"
                      max="24"
                      value={orgPolicy.expiry_months}
                      onChange={(e) =>
                        setOrgPolicy((p) => ({ ...p, expiry_months: Number(e.target.value) }))
                      }
                      className="w-24 border rounded-lg px-3 py-2 text-sm outline-none transition-all focus:ring-2 focus:ring-[#8c57ff]/40 focus:border-[#8c57ff]/40"
                    />
                    <span className="text-sm text-gray-500">months, then forfeited</span>
                  </div>
                </div>
              )}
            </div>

            <div className="flex items-center gap-3 mt-5">
              <button
                onClick={handleSaveOrgPolicy}
                disabled={!isOrgPolicyDirty}
                className="flex items-center gap-2 text-white px-4 py-2 rounded-md disabled:opacity-40 disabled:cursor-not-allowed"
                style={{ background: "linear-gradient(135deg, #2563eb, #d12465)" }}
              >
                <Save className="h-4 w-4" /> Save Company Policy
              </button>
              {orgPolicySaved && <span className="text-sm text-green-600">Saved.</span>}
              {isOrgPolicyDirty && !orgPolicySaved && (
                <span className="text-sm text-gray-400">Unsaved changes</span>
              )}
            </div>
          </div>

          {/* Search & Add Section */}
          <div className="flex flex-col sm:flex-row sm:items-center flex-wrap gap-3 mb-6 bg-white rounded-xl px-4 py-3 shadow-sm">
            <div className="flex flex-col xs:flex-row sm:flex-row items-stretch sm:items-center flex-wrap gap-3 flex-1 min-w-0">
              <div className="flex flex-col sm:flex-row items-stretch sm:items-center border border-gray-300 rounded-lg bg-white w-full sm:w-86 md:w-[300px] lg:w-[350px] overflow-hidden transition-all focus-within:ring-2 focus-within:ring-[#8c57ff]/40 focus-within:border-[#8c57ff]/40">
                <div className="flex items-center flex-1 px-3 py-2">
                  <Search className="text-gray-400 h-3 w-3 mr-2" />
                  <input
                    type="text"
                    placeholder="Search leave types..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="flex-1 outline-none text-gray-700 bg-transparent min-w-0"
                  />
                </div>

                <select
                  value={filterStatus}
                  onChange={(e) => setFilterStatus(e.target.value)}
                  className="border-t sm:border-t-0 sm:border-l border-gray-300 px-3 py-2 outline-none bg-white w-full sm:w-auto"
                >
                  <option>All</option>
                  <option value="active">Active</option>
                  <option value="inactive">Inactive</option>
                </select>
              </div>

              {(searchQuery || filterStatus !== "All") && (
                <button
                  type="button"
                  onClick={() => {
                    setSearchQuery("");
                    setFilterStatus("All");
                  }}
                  className="flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium text-red-600 border border-red-200 bg-red-50 hover:bg-red-100 transition whitespace-nowrap shrink-0 w-full sm:w-auto"
                >
                  <X size={14} />
                  Clear Filters
                </button>
              )}
            </div>

            <button
              onClick={() => {
                setCurrentLeave(null);
                setShowModal(true);
              }}
              className="flex items-center text-white px-4 py-2 rounded-lg transition-colors duration-200 w-full sm:w-auto justify-center shrink-0 sm:ml-auto"
              style={{ background: "linear-gradient(135deg, #2563eb, #d12465)" }}
            >
              <Plus className="h-4 w-4 mr-1" /> Add New
            </button>
          </div>

          {/* ------------------------------------------------------------- */}
          {/* LEAVE TYPES TABLE (with per-row carry-forward override)       */}
          {/* Only the TABLE scrolls horizontally on small screens — the    */}
          {/* pagination bar below stays fixed/full-width and never        */}
          {/* scrolls with it (it lives outside the overflow-x-auto div).  */}
          {/* ------------------------------------------------------------- */}
          <div className="bg-white rounded-xl shadow-md p-4">
            <div className="overflow-x-auto">
              <table className="min-w-[900px] w-full border-collapse text-sm">
                <thead className="bg-sky-50 rounded-t-xl">
                  <tr>
                    <th className="py-3 px-4 text-left font-semibold text-gray-600">ID</th>
                    <th className="py-3 px-4 text-left font-semibold text-gray-600">Leave Type</th>
                    <th className="py-3 px-4 text-left font-semibold text-gray-600">Monthly Limit</th>
                    <th className="py-3 px-4 text-left font-semibold text-gray-600">Description</th>
                    <th className="py-3 px-4 text-left font-semibold text-gray-600">Carry Forward Policy</th>
                    <th className="py-3 px-4 text-left font-semibold text-gray-600">Expiry</th>
                    <th className="py-3 px-4 text-left font-semibold text-gray-600">Cap</th>
                    <th className="py-3 px-4 text-left font-semibold text-gray-600">Status</th>
                    <th className="py-3 px-4 text-center font-semibold text-gray-600">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {paginatedLeaveTypes.length > 0 ? (
                    paginatedLeaveTypes.map((type, index) => {
                      const isOverridden =
                        type.carry_forward_policy != null ||
                        type.carry_forward_count != null ||
                        type.expiry_exp != null;
                      const isEditingCf = editingCfId === type.id;

                      const effective = resolveEffectivePolicy(type, savedOrgPolicy);
                      const Icon = policyMeta(effective.policy).icon;

                      return (
                        <tr
                          key={type.id}
                          className={`${index % 2 !== 0 ? "bg-[#f7f6fe]" : ""} border-b hover:bg-gray-50`}
                        >
                          <td className="py-3 px-4 text-sky-600 whitespace-nowrap">
                            {(index + 1).toString().padStart(2, "0")}
                          </td>
                          <td className="py-3 px-4 whitespace-nowrap">{type.name}</td>
                          <td className="py-3 px-4 whitespace-nowrap">{type.monthly_limit}</td>
                          <td className="py-3 px-4 min-w-[200px]">{type.description}</td>

                          {isEditingCf ? (
                            <>
                              <td className="py-2 px-4">
                                <select
                                  value={cfDraft.carry_forward_policy}
                                  onChange={(e) =>
                                    setCfDraft((d) => ({ ...d, carry_forward_policy: e.target.value }))
                                  }
                                  className="border rounded-md px-2 py-1 text-sm outline-none transition-all focus:ring-2 focus:ring-[#8c57ff]/40 focus:border-[#8c57ff]/40"
                                >
                                  {CARRY_FORWARD_POLICIES.map((o) => (
                                    <option key={o.value} value={o.value}>
                                      {o.label}
                                    </option>
                                  ))}
                                </select>
                              </td>
                              <td className="py-2 px-4">
                                {cfDraft.carry_forward_policy === "expiring" ? (
                                  <input
                                    type="number"
                                    min="1"
                                    max="24"
                                    value={cfDraft.expiry_exp}
                                    onChange={(e) =>
                                      setCfDraft((d) => ({
                                        ...d,
                                        expiry_exp: e.target.value,
                                      }))
                                    }
                                    className="w-20 border rounded-md px-2 py-1 text-sm outline-none transition-all focus:ring-2 focus:ring-[#8c57ff]/40 focus:border-[#8c57ff]/40"
                                  />
                                ) : (
                                  <span className="text-gray-400">—</span>
                                )}
                              </td>
                              <td className="py-2 px-4">
                                {cfDraft.carry_forward_policy !== "none" ? (
                                  <input
                                    type="number"
                                    min="0"
                                    step="0.5"
                                    value={cfDraft.carry_forward_count}
                                    onChange={(e) =>
                                      setCfDraft((d) => ({ ...d, carry_forward_count: e.target.value }))
                                    }
                                    placeholder="No cap"
                                    className="w-24 border rounded-md px-2 py-1 text-sm outline-none transition-all focus:ring-2 focus:ring-[#8c57ff]/40 focus:border-[#8c57ff]/40"
                                  />
                                ) : (
                                  <span className="text-gray-400">—</span>
                                )}
                              </td>
                            </>
                          ) : (
                            <>
                              <td className="py-3 px-4 whitespace-nowrap">
                                <span className="inline-flex items-center gap-1.5">
                                  <Icon className="h-3.5 w-3.5 text-gray-500" />
                                  {policyMeta(effective.policy).label}
                                  {!isOverridden && (
                                    <span className="text-[10px] uppercase tracking-wide text-gray-400 border border-gray-200 rounded px-1.5 py-0.5 ml-1">
                                      default
                                    </span>
                                  )}
                                </span>
                              </td>
                              <td className="py-3 px-4 whitespace-nowrap">
                                {effective.expiry_exp ? (
                                  `${effective.expiry_exp} months`
                                ) : (
                                  <span className="text-gray-400">—</span>
                                )}
                              </td>
                              <td className="py-3 px-4 whitespace-nowrap">
                                {effective.policy !== "none" ? (
                                  effective.carry_forward_count != null ? (
                                    `${effective.carry_forward_count} days`
                                  ) : (
                                    "No cap"
                                  )
                                ) : (
                                  <span className="text-gray-400">—</span>
                                )}
                              </td>
                            </>
                          )}

                          <td className="py-3 px-4 whitespace-nowrap">
                            <span
                              className={`px-3 py-1 rounded-full text-xs font-medium ${statusColors[type.status]}`}
                            >
                              {type.status}
                            </span>
                          </td>

                          <td className="py-3 px-4">
                            <div className="flex justify-center items-center gap-3 text-gray-600">
                              {isEditingCf ? (
                                <>
                                  <button
                                    onClick={() => saveCfOverride(type)}
                                    className="p-1.5 rounded-md bg-green-100 text-green-600 hover:bg-green-200"
                                    title="Save carry-forward override"
                                  >
                                    <Check className="h-4 w-4" />
                                  </button>
                                  <button
                                    onClick={cancelCfOverride}
                                    className="p-1.5 rounded-md bg-gray-100 text-gray-500 hover:bg-gray-200"
                                    title="Cancel"
                                  >
                                    <X className="h-4 w-4" />
                                  </button>
                                </>
                              ) : (
                                <>
                                  <Pencil
                                    className="h-4 w-4 cursor-pointer hover:text-sky-600"
                                    title="Override carry-forward policy for this leave type"
                                    onClick={() => startCfOverride(type)}
                                  />
                                  {isOverridden && (
                                    <RotateCcw
                                      className="h-4 w-4 cursor-pointer hover:text-amber-600"
                                      title="Reset to company default"
                                      onClick={() => resetCfToDefault(type)}
                                    />
                                  )}
                                  <span className="w-px h-4 bg-gray-200" />
                                  <Edit2
                                    className="h-4 w-4 cursor-pointer hover:text-sky-600"
                                    title="Edit leave type details"
                                    onClick={() => {
                                      setCurrentLeave(type);
                                      setShowModal(true);
                                    }}
                                  />
                                  <Trash2
                                    className="h-4 w-4 cursor-pointer hover:text-red-600"
                                    title="Delete leave type"
                                    onClick={() => {
                                      setDeleteId(type.id);
                                      setShowDeleteModal(true);
                                    }}
                                  />
                                </>
                              )}
                            </div>
                          </td>
                        </tr>
                      );
                    })
                  ) : (
                    <tr>
                      <td colSpan="9" className="text-center py-6 text-gray-500">
                        No leave types found.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            {/* Pagination — outside the overflow-x-auto wrapper, so it stays
                fixed/full-width on mobile instead of scrolling with the table. */}
            <div className="flex flex-col sm:flex-row justify-between items-center mt-4 gap-3">
              <div className="flex items-center space-x-2">
                <label htmlFor="recordsPerPage" className="text-gray-700 text-sm">
                  Records:
                </label>
                <select
                  id="recordsPerPage"
                  className="border border-gray-300 rounded-md px-2 py-1 text-sm outline-none transition-all focus:ring-2 focus:ring-[#8c57ff]/40 focus:border-[#8c57ff]/40"
                  value={itemsPerPage}
                  onChange={(e) => {
                    setPage(1);
                    setItemsPerPage(Number(e.target.value));
                  }}
                >
                  {[3, 5, 10, 20].map((num) => (
                    <option key={num} value={num}>
                      {num}
                    </option>
                  ))}
                </select>
              </div>

              <div className="flex items-center space-x-1">
                <button
                  onClick={() => setPage(1)}
                  disabled={page === 1}
                  className="px-2.5 py-1.5 rounded-lg bg-gray-100 text-gray-600 hover:bg-gray-200 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                >
                  <ChevronsLeft size={20} />
                </button>
                <button
                  className="px-2.5 py-1.5 rounded-lg bg-gray-100 text-gray-600 hover:bg-gray-200 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                  onClick={() => setPage((prev) => Math.max(prev - 1, 1))}
                  disabled={page === 1}
                >
                  <MdKeyboardArrowLeft size={20} />
                </button>

                {(() => {
                  let startPage = Math.max(page - 1, 1);
                  let endPage = startPage + 2;
                  if (endPage > totalPages) {
                    endPage = totalPages;
                    startPage = Math.max(endPage - 2, 1);
                  }
                  const pages = [];
                  for (let i = startPage; i <= endPage; i++) {
                    pages.push(
                      <button
                        key={i}
                        className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                          i === page
                            ? "text-white bg-[linear-gradient(135deg,#2563eb,#d12465)]"
                            : "bg-gray-100 text-gray-600 hover:bg-gray-200"
                        }`}
                        onClick={() => setPage(i)}
                      >
                        {i}
                      </button>
                    );
                  }
                  return pages;
                })()}

                <button
                  className="px-2.5 py-1.5 rounded-lg bg-gray-100 text-gray-600 hover:bg-gray-200 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                  onClick={() => setPage((prev) => Math.min(prev + 1, totalPages))}
                  disabled={page === totalPages}
                >
                  <MdKeyboardArrowRight size={20} />
                </button>
                <button
                  onClick={() => setPage(totalPages)}
                  disabled={page === totalPages}
                  className="px-2.5 py-1.5 rounded-lg bg-gray-100 text-gray-600 hover:bg-gray-200 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                >
                  <ChevronsRight size={20} />
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Add/Edit Modal — basic leave type details only. Carry-forward is
          managed inline in the table (it inherits the company default by
          default; override it there once the leave type exists). */}
      {showModal && (
        <div className="fixed inset-0 bg-black/30 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-lg w-full max-w-md p-6 relative max-h-[90vh] overflow-y-auto">
            <button
              onClick={() => {
                setShowModal(false);
                setCurrentLeave(null);
              }}
              className="absolute right-4 top-4 text-gray-500 hover:text-gray-700"
            >
              <X className="h-5 w-5" />
            </button>
            <h3 className="text-xl font-semibold mb-4">
              {currentLeave ? "Edit Leave Type" : "Add New Leave Type"}
            </h3>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-1">Leave Type Name</label>
                <input
                  name="name"
                  type="text"
                  defaultValue={currentLeave?.name || ""}
                  placeholder="Enter Leave Type"
                  className="w-full border rounded-lg px-3 py-2 outline-none transition-all focus:ring-2 focus:ring-[#8c57ff]/40 focus:border-[#8c57ff]/40"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Monthly Limit</label>
                <input
                  name="monthly_limit"
                  type="number"
                  step="0.5"
                  defaultValue={currentLeave?.monthly_limit || ""}
                  placeholder="Enter Monthly Limit"
                  className="w-full border rounded-lg px-3 py-2 outline-none transition-all focus:ring-2 focus:ring-[#8c57ff]/40 focus:border-[#8c57ff]/40"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Description</label>
                <textarea
                  name="desc"
                  rows="3"
                  defaultValue={currentLeave?.description || ""}
                  placeholder="Description"
                  className="w-full border rounded-lg px-3 py-2 outline-none transition-all focus:ring-2 focus:ring-[#8c57ff]/40 focus:border-[#8c57ff]/40"
                />
              </div>

              {!currentLeave && (
                <p className="text-xs text-gray-500 bg-gray-50 border rounded-lg px-3 py-2">
                  New leave types follow the company default carry-forward policy. You can
                  override it from the table once it's created.
                </p>
              )}

              <div>
                <label className="block text-sm font-medium mb-1">Status</label>
                <div className="flex items-center gap-4">
                  <label className="flex items-center gap-2">
                    <input
                      type="radio"
                      name="status"
                      value="active"
                      defaultChecked={currentLeave?.status === "active" || !currentLeave}
                    />
                    <span>Active</span>
                  </label>
                  <label className="flex items-center gap-2">
                    <input
                      type="radio"
                      name="status"
                      value="inactive"
                      defaultChecked={currentLeave?.status === "inactive"}
                    />
                    <span>Inactive</span>
                  </label>
                </div>
              </div>
              <div className="flex justify-end space-x-3 mt-4">
                <button
                  type="button"
                  onClick={() => setShowModal(false)}
                  className="px-4 py-2 rounded-lg border"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 rounded-lg text-white"
                  style={{ background: "linear-gradient(135deg, #2563eb, #d12465)" }}
                >
                  {currentLeave ? "Update Leave Type" : "Create New Leave Type"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Delete Modal */}
      {showDeleteModal && (
        <div className="fixed inset-0 bg-black/30 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-lg w-full max-w-sm p-6 text-center">
            <h3 className="text-lg font-semibold mb-3">Delete Leave Type?</h3>
            <p className="text-gray-600 mb-5">
              Are you sure you want to delete this leave type? This action cannot be undone.
            </p>
            <div className="flex justify-center gap-4">
              <button
                onClick={() => setShowDeleteModal(false)}
                className="px-4 py-2 rounded-lg border"
              >
                Cancel
              </button>
              <button
                onClick={handleDelete}
                className="px-4 py-2 rounded-lg bg-red-600 text-white hover:bg-red-700"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}